// src/components/appSettings/Collection/CollectionList.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiEdit2,
  FiTrash2,
  FiEye,
  FiPlus,
  FiFilter,
  FiRefreshCw,
  FiGrid,
  FiList,
  FiStar,
  FiPackage,
  FiTrendingUp,
} from "react-icons/fi";
import {
  fetchAllCollectionsThunk,
  deleteCollectionThunk,
  updateCollectionStatusThunk,
} from "../../../store/thunks/appSettingsThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";

const CollectionList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { collections, loadingList, error } = useSelector(
    (state) => state.appSettings
  );
  const { user } = useSelector((state) => state.auth);
  const { selectedBranch, branches } = useSelector((state) => state.branch);

  // Local state
  const [viewMode, setViewMode] = useState("grid");
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filters state - Initialize with selectedBranch
  const [filters, setFilters] = useState({
    search: "",
    isActive: "",
    isFeatured: "",
    branchCode: selectedBranch || "",
  });

  // Update filters when selectedBranch changes
  useEffect(() => {
    if (selectedBranch) {
      setFilters((prev) => ({
        ...prev,
        branchCode: selectedBranch,
      }));
    }
  }, [selectedBranch]);

  // Load collections function
  const loadCollections = useCallback(() => {
    // Prepare filter params - only send non-empty values
    const filterParams = {};

    if (filters.isActive !== "") {
      filterParams.isActive = filters.isActive;
    }

    if (filters.isFeatured !== "") {
      filterParams.isFeatured = filters.isFeatured;
    }

    if (filters.branchCode) {
      filterParams.branchCode = filters.branchCode;
    }

    if (filters.search) {
      filterParams.search = filters.search;
    }

    dispatch(fetchAllCollectionsThunk(filterParams));
  }, [dispatch, filters]);

  // Load collections on mount and when filters change
  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  // Handlers
  const handleDeleteCollection = async (collectionId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this collection? This action cannot be undone."
      )
    ) {
      try {
        await dispatch(deleteCollectionThunk(collectionId)).unwrap();
        toast.success("Collection deleted successfully");
        loadCollections();
      } catch (error) {
        toast.error(error.message || "Failed to delete collection");
      }
    }
  };

  const handleToggleStatus = async (collectionId, currentStatus) => {
    try {
      await dispatch(
        updateCollectionStatusThunk({
          id: collectionId,
          statusData: { isActive: !currentStatus },
        })
      ).unwrap();
      toast.success("Collection status updated successfully");
      loadCollections();
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const handleSelectCollection = (collectionId) => {
    setSelectedCollections((prev) =>
      prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCollections.length === collections.length) {
      setSelectedCollections([]);
    } else {
      setSelectedCollections(collections.map((c) => c._id));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      isActive: "",
      isFeatured: "",
      branchCode: selectedBranch || "",
    });
  };

  const getBranchName = (branchCode) => {
    const branch = branches?.find((b) => b.code === branchCode);
    return branch ? branch.name : branchCode;
  };

  const getStatusColor = (collection) => {
    if (!collection.isActive) return "gray";

    const now = new Date();
    if (collection.validFrom && new Date(collection.validFrom) > now)
      return "yellow";
    if (collection.validUntil && new Date(collection.validUntil) < now)
      return "red";

    return "green";
  };

  const getStatusText = (collection) => {
    if (!collection.isActive) return "Inactive";

    const now = new Date();
    if (collection.validFrom && new Date(collection.validFrom) > now)
      return "Scheduled";
    if (collection.validUntil && new Date(collection.validUntil) < now)
      return "Expired";

    return "Active";
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Collection Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage product collections and categories
          </p>
        </div>

        <div className="flex gap-3">
          <Button onClick={() => navigate("/app-settings/collections/new")}>
            <FiPlus className="mr-2" />
            Add Collection
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow p-3 md:p-4 lg:p-6 mb-6">
        {/* Main Filter Section */}
        <div className="space-y-4 mb-4">
          {/* Top Row: Search and Actions */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            {/* Search Input */}
            <input
              type="text"
              placeholder="Search collections..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow"
            />

            {/* Right Side Actions */}
            <div className="flex items-center gap-2 justify-between sm:justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={loadCollections}
                disabled={loadingList}
                className="shrink-0"
                aria-label="Refresh collections"
              >
                <FiRefreshCw className={loadingList ? "animate-spin" : ""} />
              </Button>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1 shrink-0">
                <button
                  className={`p-1.5 rounded transition-all ${
                    viewMode === "grid"
                      ? "bg-white shadow"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                >
                  <FiGrid />
                </button>
                <button
                  className={`p-1.5 rounded transition-all ${
                    viewMode === "list"
                      ? "bg-white shadow"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => setViewMode("list")}
                  aria-label="List view"
                >
                  <FiList />
                </button>
              </div>
            </div>
          </div>
          {/* Filter Controls Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Status Select */}
            <select
              value={filters.isActive}
              onChange={(e) => handleFilterChange("isActive", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow w-full"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {/* Featured Select */}
            <select
              value={filters.isFeatured}
              onChange={(e) => handleFilterChange("isFeatured", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow w-full"
            >
              <option value="">All Collections</option>
              <option value="true">Featured Only</option>
              <option value="false">Non-Featured</option>
            </select>

            {/* Branch Select */}
            <select
              value={filters.branchCode}
              onChange={(e) => handleFilterChange("branchCode", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow w-full"
            >
              <option value="">All Branches</option>
              {branches?.map((branch) => (
                <option key={branch.code} value={branch.code}>
                  {branch.name} ({branch.code})
                </option>
              ))}
            </select>

            {/* Clear Filters Button */}
            <Button
              variant="outline"
              onClick={handleClearFilters}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Bulk Actions Banner */}
        {selectedCollections.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-100">
            <span className="text-sm md:text-base text-blue-700 font-medium">
              {selectedCollections.length} collection
              {selectedCollections.length !== 1 ? "s" : ""} selected
            </span>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 sm:flex-none whitespace-nowrap"
              >
                Bulk Actions
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedCollections([])}
                className="flex-1 sm:flex-none whitespace-nowrap"
              >
                Clear Selection
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Collections Display */}
      {loadingList ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : collections.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FiPackage className="mx-auto text-5xl text-gray-400 mb-4" />
          <p className="text-gray-500">No collections found</p>
          <Button
            className="mt-4"
            onClick={() => navigate("/app-settings/collections/new")}
          >
            Create Your First Collection
          </Button>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.map((collection) => (
            <CollectionCard
              key={collection._id}
              collection={collection}
              selected={selectedCollections.includes(collection._id)}
              onSelect={handleSelectCollection}
              onEdit={(id) => navigate(`/app-settings/collections/edit/${id}`)}
              onDelete={handleDeleteCollection}
              onToggleStatus={handleToggleStatus}
              onView={(id) => navigate(`/app-settings/collections/${id}`)}
              getStatusColor={getStatusColor}
              getStatusText={getStatusText}
              getBranchName={getBranchName}
            />
          ))}
        </div>
      ) : (
        <CollectionListView
          collections={collections}
          selectedCollections={selectedCollections}
          onSelectAll={handleSelectAll}
          onSelect={handleSelectCollection}
          onEdit={(id) => navigate(`/app-settings/collections/edit/${id}`)}
          onDelete={handleDeleteCollection}
          onToggleStatus={handleToggleStatus}
          onView={(id) => navigate(`/app-settings/collections/${id}`)}
          getStatusColor={getStatusColor}
          getStatusText={getStatusText}
          getBranchName={getBranchName}
        />
      )}
    </div>
  );
};

// Collection Card Component
const CollectionCard = ({
  collection,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onView,
  getStatusColor,
  getStatusText,
  getBranchName,
}) => {
  const statusColor = getStatusColor(collection);
  const statusText = getStatusText(collection);

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-all duration-200">
      <div className="relative">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelect(collection._id)}
          className="absolute top-2 left-2 z-10"
        />

        {/* Collection Image Preview */}
        <div className="h-48 bg-gray-200 rounded-t-lg overflow-hidden">
          {collection.images?.thumbnail || collection.images?.hero ? (
            <img
              src={collection.images.thumbnail || collection.images.hero}
              alt={collection.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gradient-to-br from-blue-100 to-purple-100">
              <FiPackage className="text-4xl text-gray-400" />
            </div>
          )}
        </div>

        {/* Status and Featured Badges */}
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          <span
            className={`inline-flex px-2 py-1 text-xs rounded-full bg-${statusColor}-100 text-${statusColor}-800`}
          >
            {statusText}
          </span>
          {collection.isFeatured && (
            <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
              <FiStar className="mr-1" size={12} />
              Featured
            </span>
          )}
          {collection.isExclusive && (
            <span className="inline-flex px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
              Exclusive
            </span>
          )}
        </div>
      </div>

      <div className="p-4">
        <div className="mb-2">
          <h3 className="font-medium text-gray-900 line-clamp-1">
            {collection.name}
          </h3>
          {collection.shortDescription && (
            <p className="text-sm text-gray-500 line-clamp-2 mt-1">
              {collection.shortDescription}
            </p>
          )}
        </div>

        <div className="space-y-2 text-sm mb-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Slug:</span>
            <span className="font-medium text-gray-700">{collection.slug}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Priority:</span>
            <span className="font-medium">
              {collection.display?.priority || 0}
            </span>
          </div>
          {collection.targeting?.branches?.length > 0 && (
            <div className="flex items-start justify-between">
              <span className="text-gray-500">Branches:</span>
              <span className="font-medium text-right">
                {collection.targeting.branches.length === 1
                  ? getBranchName(collection.targeting.branches[0])
                  : `${collection.targeting.branches.length} branches`}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Card Style:</span>
            <span className="font-medium">
              {collection.display?.cardStyle || "STANDARD"}
            </span>
          </div>
        </div>

        {/* Analytics Quick View */}
        {collection.analytics && (
          <div className="grid grid-cols-3 gap-2 p-2 bg-gray-50 rounded-lg mb-3">
            <div className="text-center">
              <p className="text-xs text-gray-500">Views</p>
              <p className="text-sm font-semibold">
                {collection.analytics.views || 0}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Clicks</p>
              <p className="text-sm font-semibold">
                {collection.analytics.clicks || 0}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Products</p>
              <p className="text-sm font-semibold">
                {collection.productCount || 0}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(collection._id)}
            title="Edit Collection"
          >
            <FiEdit2 />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onView(collection._id)}
            title="View Details"
          >
            <FiEye />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggleStatus(collection._id, collection.isActive)}
            className={collection.isActive ? "text-red-600" : "text-green-600"}
            title={collection.isActive ? "Deactivate" : "Activate"}
          >
            {collection.isActive ? "Deactivate" : "Activate"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(collection._id)}
            className="text-red-600 hover:bg-red-50"
            title="Delete Collection"
          >
            <FiTrash2 />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Collection List View Component
const CollectionListView = ({
  collections,
  selectedCollections,
  onSelectAll,
  onSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onView,
  getStatusColor,
  getStatusText,
  getBranchName,
}) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left">
            <input
              type="checkbox"
              onChange={onSelectAll}
              checked={selectedCollections.length === collections.length}
            />
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Collection
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Slug
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Priority
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Branches
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {collections.map((collection) => {
          const statusColor = getStatusColor(collection);
          const statusText = getStatusText(collection);

          return (
            <tr key={collection._id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedCollections.includes(collection._id)}
                  onChange={() => onSelect(collection._id)}
                />
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div className="h-10 w-16 bg-gray-200 rounded overflow-hidden mr-3">
                    {(collection.images?.thumbnail ||
                      collection.images?.hero) && (
                      <img
                        src={
                          collection.images.thumbnail || collection.images.hero
                        }
                        alt={collection.name}
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      {collection.name}
                      {collection.isFeatured && (
                        <FiStar className="text-yellow-500" size={14} />
                      )}
                    </div>
                    {collection.shortDescription && (
                      <div className="text-sm text-gray-500 line-clamp-1">
                        {collection.shortDescription}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">{collection.slug}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {collection.display?.priority || 0}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {collection.targeting?.branches?.length > 0
                    ? collection.targeting.branches.length === 1
                      ? getBranchName(collection.targeting.branches[0])
                      : `${collection.targeting.branches.length} branches`
                    : "All branches"}
                </div>
              </td>
              <td className="px-6 py-4">
                <span
                  className={`px-2 py-1 text-xs rounded-full bg-${statusColor}-100 text-${statusColor}-800`}
                >
                  {statusText}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(collection._id)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    onClick={() => onView(collection._id)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <FiEye />
                  </button>
                  <button
                    onClick={() => onDelete(collection._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default CollectionList;
